import React, { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { parse, format, isValid } from "date-fns";
import { CalendarIcon } from "../Icons";
import { Label } from "./DateInput.styled";

interface DateInputProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      label,
      placeholder = "MM/DD/YYYY",
      value,
      onChange,
      disabled = false,
      fullWidth = true,
      width,
      name,
      error = false,
      helperText,
      required = false,
    },
    _ref,
  ) => {
    // Convert string value (YYYY-MM-DD) to Date object
    const dateValue = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
    const validDateValue = dateValue && isValid(dateValue) ? dateValue : null;

    // Handle date change from DatePicker
    const handleDateChange = (newDate: Date | null) => {
      if (onChange) {
        // Convert Date to string format (YYYY-MM-DD) for form compatibility
        const formattedValue =
          newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";

        // Create a synthetic event to maintain compatibility with existing form handlers
        const syntheticEvent = {
          target: {
            name: name || "",
            value: formattedValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onChange(syntheticEvent);
      }
    };

    return (
      <Box sx={{ width: fullWidth ? "100%" : width }}>
        {label && (
          <Label>
            {label}
            {required && "*"}
          </Label>
        )}
        <DatePicker
          value={validDateValue}
          onChange={handleDateChange}
          disabled={disabled}
          slots={{
            openPickerIcon: CalendarIcon,
          }}
          slotProps={{
            textField: {
              placeholder: placeholder,
              error: error,
              fullWidth: true,
              sx: {
                "& .MuiOutlinedInput-root": {
                  height: "48px",
                  borderRadius: "8px",
                  backgroundColor: error
                    ? "background.red"
                    : "background.secondary",
                  fontFamily: "Nunito, sans-serif",
                  fontSize: "16px",
                  "& fieldset": {
                    borderColor: error ? "status.error" : "border.medium",
                    borderWidth: error ? "2px" : "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: error ? "status.error" : "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: error ? "status.error" : "primary.main",
                    borderWidth: error ? "2px" : "1px",
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Nunito, sans-serif",
                  fontSize: "16px",
                  "&::placeholder": {
                    opacity: 1,
                  },
                },
              },
            },
            openPickerButton: {
              sx: {
                color: "text.secondary",
                marginRight: 0,
                padding: 0,
              },
            },
          }}
        />
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? "status.error" : "text.secondary",
              fontSize: "12px",
              mt: 0.5,
              display: "block",
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  },
);

DateInput.displayName = "DateInput";

export default DateInput;
